import {
  booleanAttribute,
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  Renderer2
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { isValidDateString, isValidISODate } from '@mng/mng-ts-helpers';

export const DATE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MngDateFieldDirective),
  multi: true
};

export const DATE_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MngDateFieldDirective),
  multi: true
};

/**
 * The accessor for writing a value and listening to changes on a date input element
 *
 *  ### Example
 *   `<input mngDateField type="text" [formControl]="modelItem.control">`
 */
@Directive({
  selector: '[mngDateField]',
  providers: [DATE_VALUE_ACCESSOR, DATE_VALIDATOR]
})
export class MngDateFieldDirective implements ControlValueAccessor, Validator {
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  viewValue: string;

  // Flag für die Validierung der Eingabe -> wenn false wird der eingegebene bei einem Eingabefehler so wie er ist
  // in das Model geschrieben und die Validierung muss anderswo passieren
  @Input({ transform: booleanAttribute })
  dateValidationRequired = true;

  @Input({ transform: booleanAttribute })
  allowEmpty = true;

  // mngDateField wird verwendet um eine Date Instanz vom eingegeben Wert zu erhalten
  @HostListener('change', ['$event.target.value']) onChange = (value: any) => {
    return;
  };

  @HostListener('input', ['$event.target.value']) onInput = this.onChange;

  @HostListener('blur', []) onTouched = () => {
    return;
  };

  /**
   * Wert --> HTML Element
   */
  writeValue(value: string) {
    // MODEL -> VIEW
    const viewValue = fromIsoDateString(value);
    this.viewValue = viewValue;
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', viewValue);
  }

  /**
   *
   */
  registerOnChange(fn: (_: any) => void) {
    // VIEW -> MODEL
    this.onChange = (value) => {
      this.viewValue = value;

      if (!this.dateValidationRequired) {
        const valid: boolean = this.isValidDateString(value);
        if (!valid) {
          // Invaliden Wert so wie er ist in das Model zurückschreiben
          // -> Die Validierung muss anderswo passieren
          fn(value);
          return;
        }
      }

      const isoDate = value === '' ? null : toIsoDateString(value);
      fn(isoDate);
    };
    this.onInput = this.onChange;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  isValidDateString(value: string): boolean {
    let valid: boolean = isValidDateString(value);

    if (valid) {
      valid = isValidISODate(toIsoDateString(this.viewValue));
    }

    return valid;
  }

  isExisting(value: string): boolean {
    if (this.allowEmpty) {
      return true;
    } else {
      return !!value;
    }
  }

  validate(c: AbstractControl): { [key: string]: any } {
    if (!this.dateValidationRequired) {
      return null;
    } else {
      const validFormat = this.isValidDateString(this.viewValue);
      const validRequired = this.isExisting(this.viewValue);
      if (!validRequired) {
        return { 'Eine Eingabe ist erforderlich': true };
      } else if (validRequired && !validFormat) {
        return { 'Datum bitte im Format TT.MM.JJJJ eingeben': true };
      } else {
        return null;
      }
    }
  }
}

function fromIsoDateString(value: string): string {
  if (value && value.length === 10) {
    const parts = value.split('-');
    const result = `${parts[2]}.${parts[1]}.${parts[0]}`;
    return result;
  } else {
    return null;
  }
}

function toIsoDateString(value: string): string {
  if (value && value.length === 10) {
    const parts = value.split('.');
    const result = `${parts[2]}-${parts[1]}-${parts[0]}`;
    return result;
  } else {
    return null;
  }
}
